import React, { useEffect, useState, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import socket from "../../helper/socket";
import UserImage from "../../helper/images/customer.png";
import { useAuth } from "../../context/AuthContext";
import { getOrCreateChatByContext, createMessage } from "../../api/common/chat";
import { useTranslation } from "../../hooks/useTranslation";

function Chat() {
  const { userData } = useAuth();
  const { t } = useTranslation();
  const [step, setStep] = useState("selectType");
  const [contextType, setContextType] = useState("");
  const [contextId, setContextId] = useState("");
  const [reason, setReason] = useState("");
  const [chatId, setChatId] = useState(null);
  const [messageText, setMessageText] = useState("");
  const [messages, setMessages] = useState([]);
  const [reasonDetail, setReasonDetail] = useState("");

  // Initialize welcome message
  useEffect(() => {
    setMessages([{ from: "bot", text: t("chat").welcomeMessage }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cards configuration
  const cards = useMemo(() => [
    {
      key: "order",
      title: t("chat").cards.order.title,
      desc: t("chat").cards.order.description,
      icon: "📦",
    },
    {
      key: "table",
      title: t("chat").cards.tableBooking.title,
      desc: t("chat").cards.tableBooking.description,
      icon: "🍽️",
    },
    {
      key: "restaurant",
      title: t("chat").cards.restaurantBooking.title,
      desc: t("chat").cards.restaurantBooking.description,
      icon: "🏨",
    },
  ], [t]);

  // Map contextType to display name for placeholder
  const getContextTypeName = (type) => {
    const card = cards.find(c => c.key === type);
    return card ? card.title : type;
  };

  const queryClient = useQueryClient();

  const validateContext = useMutation({
    mutationFn: ({ contextId, contextType, reason, reasonDetail }) =>
      getOrCreateChatByContext({
        contextId,
        contextType,
        reason,
        reasonDetail,
      }),
    onSuccess: (data) => {
      setChatId(data.chatId);
      socket.emit("joinChat", data.chatId);
      queryClient.invalidateQueries(["allChats"]);
      setMessages((prev) => [
        ...prev,
        { from: "user", text: contextId },
        { from: "bot", text: t("chat").idValidated + reason },
        { from: "bot", text: t("chat").waitForAdmin },
      ]);
      setStep("waiting");
    },
    onError: (error) => {
      setMessages((prev) => [
        ...prev,
        {
          from: "bot",
          text:
            error?.response?.data?.message ||
            t("chat").invalidId,
        },
      ]);
    },
  });

  const handleValidate = () => {
    if (contextType && contextId && reason && reasonDetail) {
      validateContext.mutate({ contextType, contextId, reason, reasonDetail });
      setMessages((prev) => [
        ...prev,
        { from: "user", text: `📝 ${reasonDetail}` },
      ]);
    }
  };

  useEffect(() => {
    socket.on("receiveMessage", (newMessage) => {
      setMessages((prev) => [
        ...prev,
        {
          from: newMessage.senderId === userData.id ? "user" : "admin",
          text: newMessage.content,
        },
      ]);
      if (step === "waiting") setStep("chat");
    });

    return () => socket.off("receiveMessage");
  }, [step]);

  // Send message
  const sendMessage = async () => {
    if (!messageText.trim() || !chatId) return;
    await createMessage({ chatId, content: messageText });
    setMessages((prev) => [...prev, { from: "user", text: messageText }]);
    setMessageText("");
  };

  return (
    <div className="w-full rounded-[20px] p-[30px]">
      {/* Chat Messages */}
      <div className="overflow-y-auto h-[350px] pt-[22px] pb-[44px] flex flex-col gap-[20px]">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${
              msg.from === "user"
                ? "justify-end"
                : msg.from === "admin"
                ? "justify-start"
                : "justify-center"
            }`}
          >
            {msg.from === "admin" && (
              <img
                src={UserImage}
                className="w-[43px] h-[43px] rounded-full mr-[10px]"
              />
            )}
            <div
              className={`py-[13px] px-[17px] border border-[#D9D9D9] rounded-[10px] max-w-[497px] ${
                msg.from === "user" ? "bg-[#FFF3EF]" : "bg-[#F1F1F1]"
              }`}
            >
              <p className="text-base leading-[26px] text-black break-words">
                {msg.text}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Step: Select Type */}
      {step === "selectType" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 border-t pt-6">
          {cards.map((item) => (
            <button
              key={item.key}
              onClick={() => {
                setContextType(item.key);
                setStep("intro");
                setMessages((prev) => [
                  ...prev,
                  { from: "bot", text: t("chat").enterId.replace("{{title}}", item.title) },
                ]);
              }}
              className="flex flex-col items-start p-6 bg-white border border-gray-300 rounded-xl shadow-sm hover:shadow-md transition-shadow hover:border-green-500"
            >
              <span className="text-3xl mb-2">{item.icon}</span>
              <h3 className="text-lg font-semibold text-gray-800">
                {item.title}
              </h3>
              <p className="text-sm text-gray-500">{item.desc}</p>
            </button>
          ))}
        </div>
      )}

      {/* Step: Enter ID */}
      {step === "intro" && (
        <div className="grid grid-cols-[1fr_auto] gap-4 pt-4 border-t">
          <input
            placeholder={t("chat").idPlaceholder.replace("{{type}}", getContextTypeName(contextType))}
            value={contextId}
            onChange={(e) => setContextId(e.target.value)}
            className="p-4 border rounded"
          />
          <button
            onClick={() => setStep("reason")}
            className="bg-green-600 text-white px-4 rounded"
          >
            {t("chat").next}
          </button>
        </div>
      )}

      {/* Step: Enter Reason */}
      {step === "reason" && (
        <div className="grid grid-cols-1 gap-4 pt-4 border-t">
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="p-4 border rounded"
          >
            <option value="">{t("chat").selectReason}</option>
            <option value="Payment Issue">{t("chat").reasons.paymentIssue}</option>
            <option value="Modify Booking">{t("chat").reasons.modifyBooking}</option>
            <option value="Cancel Booking">{t("chat").reasons.cancelBooking}</option>
            <option value="Other">{t("chat").reasons.other}</option>
          </select>
          <button
            onClick={() => setStep("reasonDetail")}
            className="bg-green-600 text-white px-4 py-2 rounded"
          >
            {t("chat").next}
          </button>
        </div>
      )}

      {step === "reasonDetail" && (
        <div className="grid grid-cols-1 gap-4 pt-4 border-t">
          <textarea
            rows={4}
            value={reasonDetail}
            onChange={(e) => setReasonDetail(e.target.value)}
            placeholder={t("chat").reasonDetailPlaceholder}
            className="p-4 border rounded resize-none"
          />
          <button
            onClick={handleValidate}
            className="bg-green-600 text-white px-4 py-2 rounded"
            disabled={!reasonDetail.trim()}
          >
            {t("chat").submit}
          </button>
        </div>
      )}

      {/* Step: Waiting for Admin */}
      {step === "waiting" && (
        <p className="pt-[10px] text-sm text-center text-gray-500">
          {t("chat").waitingForAdmin}
        </p>
      )}

      {/* Step: Chat Input */}
      {step === "chat" && (
        <div className="grid grid-cols-[1fr_auto] gap-[16px] pt-[26px] border-t border-[#EEEEEE]">
          <input
            type="text"
            placeholder={t("chat").typeHere}
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            className="p-4 bg-white border border-[#D9D9D9] rounded-[10px] w-full focus:outline-none"
          />
          <button
            onClick={sendMessage}
            className="inline-block rounded-[2px] bg-[#4CAF50] text-base text-white font-bold py-[14px] px-[38px] green-shadow hover:bg-[#209b25]"
          >
            {t("chat").send}
          </button>
        </div>
      )}
    </div>
  );
}

export default Chat;
